import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import type {
  CustomerRecord,
  CreateCustomerPayload,
} from '../../../api/customers/customersApi';

type CustomerFormModalProps = {
  customer: CustomerRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateCustomerPayload) => Promise<{ success: boolean; message?: string }>;
};

const emptyForm: CreateCustomerPayload = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  country: '',
  is_verified: false,
  password: '',
  profile_full_name: '',
  profile_address: '',
  preferred_language: '',
  emergency_contact: '',
};

const inputClass =
  'w-full rounded-[10px] border border-[#E0E0E0] bg-white px-3 py-2 font-host text-[14px] text-[#1A1A1A] outline-none focus:border-[#FF6A1A]';
const labelClass = 'font-host text-[13px] font-medium text-[#757575]';

export const CustomerFormModal = ({
  customer,
  isOpen,
  onClose,
  onSubmit,
}: CustomerFormModalProps) => {
  const [form, setForm] = useState<CreateCustomerPayload>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(customer);

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setForm(
      customer
        ? {
            username: customer.username,
            email: customer.email,
            first_name: customer.first_name,
            last_name: customer.last_name,
            phone: customer.phone,
            country: customer.country,
            is_verified: customer.is_verified,
            profile_full_name: customer.profile_full_name,
            profile_address: customer.profile_address,
            preferred_language: customer.preferred_language,
            emergency_contact: customer.emergency_contact,
          }
        : emptyForm
    );
  }, [isOpen, customer]);

  if (!isOpen) return null;

  const update = <K extends keyof CreateCustomerPayload>(
    field: K,
    value: CreateCustomerPayload[K]
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.username?.trim() || !form.email?.trim()) {
      setError('Username and email are required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const payload = { ...form };
    if (isEditMode) delete payload.password;

    const result = await onSubmit(payload);
    setIsSubmitting(false);

    if (!result.success) {
      setError(result.message || 'Something went wrong.');
      return;
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div
        className="flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[24px] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between rounded-t-[24px] border-b border-[#F0F0F0] bg-white px-6 py-5">
          <h2 className="font-host text-[20px] font-bold text-[#1A1A1A]">
            {isEditMode ? 'Edit Customer' : 'New Customer'}
          </h2>
          <button
            onClick={onClose}
            className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#1A1A1A] text-white transition-colors hover:bg-[#333333]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
          {error && (
            <p className="rounded-[8px] bg-red-50 px-3 py-2 font-host text-[13px] text-red-600">
              {error}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelClass}>First Name</label>
              <input
                className={inputClass}
                value={form.first_name || ''}
                onChange={(e) => update('first_name', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Last Name</label>
              <input
                className={inputClass}
                value={form.last_name || ''}
                onChange={(e) => update('last_name', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Username *</label>
              <input
                className={inputClass}
                value={form.username}
                onChange={(e) => update('username', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Email *</label>
              <input
                className={inputClass}
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Phone</label>
              <input
                className={inputClass}
                value={form.phone || ''}
                onChange={(e) => update('phone', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Country</label>
              <input
                className={inputClass}
                value={form.country || ''}
                onChange={(e) => update('country', e.target.value)}
              />
            </div>
          </div>

          {!isEditMode && (
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Password (optional)</label>
              <input
                className={inputClass}
                type="password"
                value={form.password || ''}
                onChange={(e) => update('password', e.target.value)}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={labelClass}>Address</label>
            <input
              className={inputClass}
              value={form.profile_address || ''}
              onChange={(e) => update('profile_address', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Preferred Language</label>
              <input
                className={inputClass}
                value={form.preferred_language || ''}
                onChange={(e) => update('preferred_language', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Emergency Contact</label>
              <input
                className={inputClass}
                value={form.emergency_contact || ''}
                onChange={(e) => update('emergency_contact', e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 font-host text-[14px] text-[#1A1A1A]">
            <input
              type="checkbox"
              checked={Boolean(form.is_verified)}
              onChange={(e) => update('is_verified', e.target.checked)}
            />
            Verified
          </label>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#F0F0F0] px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-[10px] px-4 py-2 font-host text-[14px] font-medium text-[#757575] hover:bg-[#F2F2F2]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 rounded-[10px] bg-[#FF6A1A] px-4 py-2 font-host text-[14px] font-medium text-white hover:bg-[#e85f16] disabled:opacity-60"
          >
            {isSubmitting && <Loader2 className="animate-spin" size={16} />}
            {isEditMode ? 'Save Changes' : 'Create Customer'}
          </button>
        </div>
      </div>
    </div>
  );
};
